import { useState } from 'react';

import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useCashPools } from '@/hooks/cash-pool';
import { useAssignCashPool } from '@/hooks/trades';
import { canPerform } from '@/features/trades/hooks/trade-actions';
import { getAgreementType } from '@/types';
import type { PositionDetail } from '@/types/trades.types';
import { cashPoolOptionLabel } from '@/utils/trades/cash-pool-display';
import { todayUtcKey } from '@/utils/format';
import { eligibleCashPoolParams, GMSLA_POOL_INTEREST_FROM } from '@/utils/trades/cash-pool-rate';

interface AssignCashPoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  trade: PositionDetail;
}

/**
 * Assign cash pool: the pool a trade booked before it was mandatory never got. Offers the pools
 * Trade Entry would offer this trade; the backend re-checks them. Mounted only while open, so the
 * mutation and the pick start fresh on every opening.
 */
export function AssignCashPoolModal({ isOpen, onClose, trade }: AssignCashPoolModalProps) {
  const { mutate, isPending, error } = useAssignCashPool();
  const [selected, setSelected] = useState<number | null>(null);
  const {
    data: pools = [],
    isLoading,
    isError,
  } = useCashPools(eligibleCashPoolParams(trade.counterparty_id, trade.trade_type), {
    // The "Create one" link opens Collateral Manager in another tab.
    staleTime: 0,
  });

  const isRepo = getAgreementType(trade.trade_type) === 'gmra';
  const noPools = !isLoading && !isError && pools.length === 0;
  const poolAccrues = todayUtcKey() >= GMSLA_POOL_INTEREST_FROM;

  const handleConfirm = () => {
    if (selected === null || !canPerform(trade, 'assign_cash_pool')) return;
    mutate({ id: trade.id, cashPool: selected }, { onSuccess: onClose });
  };

  return (
    <ConfirmDialog
      show={isOpen}
      onClose={onClose}
      title="Assign cash pool"
      confirmLabel="Assign"
      isLoading={isPending}
      onConfirm={handleConfirm}
      confirmDisabled={selected === null}
      error={error}
      errorFallback="Could not assign the cash pool. Please try again."
    >
      <p className="text-sm text-gray-500">
        Position <strong>{trade.id}</strong> ({trade.symbol_id}) was booked without a cash pool.
        Pick the {trade.counterparty_name} pool that holds its cash collateral.
      </p>

      <label htmlFor="assign-cash-pool" className="mt-4 block text-xs font-medium text-gray-700">
        Cash Pool
      </label>
      <select
        id="assign-cash-pool"
        value={selected ?? ''}
        onChange={(e) => setSelected(e.target.value ? Number(e.target.value) : null)}
        disabled={isLoading || isError || noPools}
        aria-describedby={
          isError ? 'assign-cash-pool-error' : noPools ? 'assign-cash-pool-hint' : undefined
        }
        className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
      >
        <option value="">{isLoading ? 'Loading cash pools…' : 'Select Cash Pool'}</option>
        {pools.map((pool) => (
          <option key={pool.id} value={pool.id}>
            {cashPoolOptionLabel(pool)}
          </option>
        ))}
      </select>
      {isError && (
        <p id="assign-cash-pool-error" className="mt-1 text-xs text-red-600" role="alert">
          Could not load cash pools. Retry - this is not &ldquo;no pool exists&rdquo;.
        </p>
      )}
      {noPools && (
        <p id="assign-cash-pool-hint" className="mt-1 text-xs text-gray-500">
          No {isRepo ? 'GMRA' : 'GMSLA'} pool for this counterparty.{' '}
          <a
            href="/collateral"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Create one in Collateral Manager
          </a>
        </p>
      )}

      <p className="mt-4 text-xs text-gray-600" aria-live="polite">
        {isRepo
          ? 'Only the link changes: the collateral reaches the pool with the next margin call, ' +
            "which is called against the pool's current balance."
          : (poolAccrues
              ? "From today the pool accrues the interest on the trade's cash, and the trade " +
                'accrues the fee only.'
              : "From today the Collateral Rate % follows the pool's rate; from " +
                `${GMSLA_POOL_INTEREST_FROM} the pool accrues the interest itself and the trade ` +
                'the fee only.') +
            ' The cash BackOffice holds for the trade enters the pool now, in the pool currency ' +
            'at the closing rate; the next Run MTM brings the pool and BackOffice to the marked ' +
            'value together.'}{' '}
        Earlier days are not recalculated, and nothing is sent to BackOffice.
      </p>
    </ConfirmDialog>
  );
}
